import { Pagination } from '@typings/common';
import {
  DataSourceParams,
  Fetcher,
  Provider,
} from 'components/shared/data/DataGrid/DataGrid.types';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { useCallback, useEffect, useReducer } from 'react';
import { pickBy, identity, map, omit } from 'lodash';

const getTotal = (total: number, fetchedItemsCount?: number): number => {
  if (fetchedItemsCount) {
    return fetchedItemsCount === total ? total : -1;
  }
  return total;
};

// Wrapper to convert AG Grid v34's success() API to our successCallback interface
const wrapSuccessCallback = <T>(params: IServerSideGetRowsParams<T>) => {
  return (rows: T[], rowCount: number) => {
    params.success({ rowData: rows, rowCount });
  };
};

export const usePagination = <T>(
  gridApi: any,
  fetcher: Fetcher<T>,
  provider: Provider<T>,
  { limit, total }: Pagination,
  fetchedItemsCount?: number,
) => {
  const [pageHandlers, setPageHandler] = useReducer((list: Record<string, any>, action: any) => {
    if (action.type === 'add') {
      return pickBy({ ...list, [action.page]: action.handler }, identity);
    } else if (action.type === 'remove') {
      return omit(list, action.page);
    } else if (action.type === 'reset') {
      return {};
    }
    return list;
  }, {} as Record<string, any>);

  useEffect(() => {
    const getRows = (params: IServerSideGetRowsParams<T>) => {
      const page = params.request.startRow / limit + 1;
      
      // Create wrapped params with successCallback for backwards compatibility
      const wrappedParams: DataSourceParams<T> = {
        ...params,
        successCallback: wrapSuccessCallback(params),
      };
      
      fetcher(page, wrappedParams);

      setPageHandler({
        type: 'add',
        page: page.toString(),
        endRow: params.request.endRow,
        handler: wrapSuccessCallback(params),
      });
    };

    if (gridApi && fetcher) {
      // AG Grid 34+ uses setGridOption instead of setServerSideDatasource
      if (typeof gridApi.setGridOption === 'function') {
        gridApi.setGridOption('serverSideDatasource', { getRows });
      } else if (typeof gridApi.setServerSideDatasource === 'function') {
        gridApi.setServerSideDatasource({ getRows });
      }
    }
  }, [fetcher, gridApi, limit]);

  useEffect(() => {
    map(pageHandlers, (handler: any, page) => {
      const rows = provider(page);
      if (rows && typeof handler === 'function') {
        handler(rows, getTotal(total, fetchedItemsCount));
        setPageHandler({ type: 'remove', page });
      }
    });
  }, [pageHandlers, provider, total, fetchedItemsCount]);

  const resetPagination = useCallback(() => {
    setPageHandler({ type: 'reset' });
    if (typeof gridApi?.refreshServerSide === 'function') {
      gridApi.refreshServerSide({ purge: true });
    }
  }, [gridApi]);

  return {
    resetPagination,
  };
};
